import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { StaggerInView, StaggerInViewItem } from "../tools/Animations";
import { HealthProductGroupCard } from "./HealthProductGroupCard";
import { HealthSectionHeader } from "./HealthSectionHeader";
import {
    healthContentPadX,
    healthSectionBlock,
    healthSectionInnerStyle,
    healthSectionOuter,
    healthSectionYCompact,
} from "./healthLayout";
import {
    AIMMA_PRODUCT_SUITE_GROUP_IDS,
    AimmaProductSuiteGroupId,
    getLocalized,
    HealthLang,
} from "./healthTypes";
import {
    HEALTH_BG_GREY,
    healthProductSuiteGrid,
    healthProductSuiteGroupFullWidth,
    healthProductSuiteGroupStretch,
} from "./healthVisualStyles";

interface HealthProductSuiteSectionProps {
    data: DataKeys;
}

function groupSortIndex(id: string): number {
    const index = AIMMA_PRODUCT_SUITE_GROUP_IDS.indexOf(
        id as AimmaProductSuiteGroupId
    );
    return index === -1 ? AIMMA_PRODUCT_SUITE_GROUP_IDS.length : index;
}

export function HealthProductSuiteSection({ data }: HealthProductSuiteSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const groups: DataKeys[] = data?.groups ?? [];
    const sectionId = (data?.id as string) || "product-suite";
    const eyebrow =
        getLocalized(data?.eyebrow, hLang) || "Aimma Agent Ecosystem";
    const title = getLocalized(data?.title, hLang);
    const subtitle = getLocalized(data?.subtitle, hLang);

    const sortedGroups = [...groups].sort(
        (a, b) => groupSortIndex(a.id as string) - groupSortIndex(b.id as string)
    );

    return (
        <section
            id={sectionId}
            aria-labelledby={`${sectionId}-title`}
            className={`${healthSectionOuter} scroll-mt-[var(--health-nav-offset,5rem)] text-left [text-align:left]`}
            style={{ backgroundColor: HEALTH_BG_GREY }}
        >
            <div className="m-auto w-full" style={healthSectionInnerStyle}>
                <div className={`${healthContentPadX} ${healthSectionYCompact}`}>
                        <div className={healthSectionBlock}>
                            <HealthSectionHeader
                                title={title}
                                titleId={`${sectionId}-title`}
                                eyebrow={eyebrow}
                                subtitle={subtitle}
                                className="mb-0 lg:mb-0"
                            />
                            <StaggerInView className={healthProductSuiteGrid}>
                                {sortedGroups.map((group) => {
                                    const groupId = group.id as string;
                                    const isDoctorRow = groupId === "doctor";
                                    const isPairedRow =
                                        groupId === "patient" ||
                                        groupId === "nurse" ||
                                        groupId === "educator" ||
                                        groupId === "nursing-robot";
                                    return (
                                        <StaggerInViewItem
                                            key={groupId}
                                            direction="bottom"
                                            className={
                                                isDoctorRow
                                                    ? healthProductSuiteGroupFullWidth
                                                    : isPairedRow
                                                      ? healthProductSuiteGroupStretch
                                                      : undefined
                                            }
                                        >
                                            <HealthProductGroupCard
                                                group={group}
                                                lang={hLang}
                                                className={
                                                    isDoctorRow
                                                        ? undefined
                                                        : isPairedRow
                                                          ? healthProductSuiteGroupStretch
                                                          : undefined
                                                }
                                            />
                                        </StaggerInViewItem>
                                    );
                                })}
                            </StaggerInView>
                        </div>
                </div>
            </div>
        </section>
    );
}
